import React, { useEffect, useMemo, useState } from 'react';
import { Container, Grow, Grid, Paper } from '@material-ui/core';
import { Link, useLocation } from 'react-router-dom';

import {
  getEvents as getArchivedEvents,
  getMarineSpeciesChecklist,
  getMarineWildlifeContext,
  getUpcomingEvents,
} from '../../api';
import {
  buildThemeEventPayload,
  buildThemeHref,
  filterEventsForTheme,
  getDiscoveryTheme,
  getThemeRequestFilters,
  readDiscoveryTheme,
} from '../../lib/discoveryThemes';
import { EventLearningCard } from '../Events/EventLearningCard';
import { ThemeSwitch } from '../Theme/ThemeSwitch';

const INTENT_OPTIONS = [
  { value: 'observe', label: 'Observe' },
  { value: 'wildlife', label: 'Wildlife' },
  { value: 'tour', label: 'Tour' },
  { value: 'science', label: 'Science' },
];

function defaultDate() {
  return new Date().toISOString().slice(0, 10);
}

function prettyDate(value) {
  if (!value) return 'Timing TBD';
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCoordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : 'TBD';
}

function buildArchiveEventPayload(items) {
  const events = items.map((item) => ({
    ...item,
    title: item.title || item.name || item.event_type || 'Untitled event',
    summary:
      item.summary ||
      item.description ||
      item.reason ||
      'No summary available yet.',
    starts_at: item.starts_at || item.start_ts || item.start_date || '',
  }));

  return {
    summary: {
      event_count: events.length,
      active_count: events.filter((item) => item.status === 'active').length,
      upcoming_count: events.filter((item) => item.status !== 'active').length,
    },
    events,
  };
}

export const DiscoveryPlanner = ({ lat, lng }) => {
  const location = useLocation();
  const activeTheme = readDiscoveryTheme(location.search);
  const themeConfig = getDiscoveryTheme(activeTheme);
  const [manualLat, setManualLat] = useState(lat ?? 36.95);
  const [manualLng, setManualLng] = useState(lng ?? -122.1);
  const [date, setDate] = useState(defaultDate());
  const [days, setDays] = useState(14);
  const [radiusKm, setRadiusKm] = useState(50);
  const [intent, setIntent] = useState('observe');
  const [speciesQuery, setSpeciesQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventsPayload, setEventsPayload] = useState(null);
  const [checklistPayload, setChecklistPayload] = useState(null);
  const [wildlifePayload, setWildlifePayload] = useState(null);
  const themeFilters = useMemo(() => getThemeRequestFilters(activeTheme), [activeTheme]);

  useEffect(() => {
    if (typeof lat === 'number' && Number.isFinite(lat)) setManualLat(lat);
  }, [lat]);

  useEffect(() => {
    if (typeof lng === 'number' && Number.isFinite(lng)) setManualLng(lng);
  }, [lng]);

  useEffect(() => {
    if (!Number.isFinite(Number(manualLat)) || !Number.isFinite(Number(manualLng)) || !date) {
      return;
    }

    let active = true;
    setLoading(true);
    setError('');

    const upcomingRequest = getUpcomingEvents({
      lat: Number(manualLat),
      lon: Number(manualLng),
      radiusKm,
      days,
      interests: themeFilters.interests,
      categories: themeFilters.categories,
    })
      .then((response) => ({
        ...response,
        data: buildThemeEventPayload(response.data, activeTheme),
      }))
      .catch(async () => {
      const { data } = await getArchivedEvents(1);
      const baseItems = data?.data || [];
      const themedItems = filterEventsForTheme(baseItems, activeTheme);
      return { data: buildArchiveEventPayload(themedItems.slice(0, 8)) };
      });

    if (activeTheme === 'wildlife') {
      Promise.all([
        upcomingRequest,
        getMarineSpeciesChecklist({
          lat: Number(manualLat),
          lon: Number(manualLng),
          date,
          intent,
        }),
        getMarineWildlifeContext({
          lat: Number(manualLat),
          lon: Number(manualLng),
          date,
        }),
      ])
        .then(([eventsRes, checklistRes, wildlifeRes]) => {
          if (!active) return;
          setEventsPayload(eventsRes.data);
          setChecklistPayload(checklistRes.data);
          setWildlifePayload(wildlifeRes.data);
        })
        .catch((err) => {
          if (!active) return;
          setError(err?.response?.data?.error || err?.message || 'Failed to load discovery context.');
        })
        .finally(() => {
          if (!active) return;
          setLoading(false);
        });
    } else {
      upcomingRequest
        .then((eventsRes) => {
          if (!active) return;
          setEventsPayload(eventsRes.data);
          setChecklistPayload(null);
          setWildlifePayload(null);
        })
        .catch((err) => {
          if (!active) return;
          setError(err?.response?.data?.error || err?.message || 'Failed to load discovery context.');
        })
        .finally(() => {
          if (!active) return;
          setLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, [activeTheme, date, days, intent, manualLat, manualLng, radiusKm, themeFilters]);

  const nearbyEvents = Array.isArray(eventsPayload?.events) ? eventsPayload.events.slice(0, 6) : [];
  const strataCards =
    activeTheme === 'wildlife'
      ? Array.isArray(checklistPayload?.strata_cards)
        ? checklistPayload.strata_cards.slice(0, 4)
        : []
      : themeConfig.planner.staticCards || [];
  const wildlifeNotes = Array.isArray(wildlifePayload?.nearby_pattern_notes)
    ? wildlifePayload.nearby_pattern_notes
    : [];
  const seasonalNotes = Array.isArray(wildlifePayload?.seasonal_context?.notes)
    ? wildlifePayload.seasonal_context.notes
    : [];
  const noteItems =
    activeTheme === 'wildlife'
      ? seasonalNotes.concat(wildlifeNotes)
      : themeConfig.planner.staticNotes || [];

  const querySummary = useMemo(
    () => [
      `Place ${formatCoordinate(manualLat)}, ${formatCoordinate(manualLng)}`,
      `Date ${date || 'TBD'}`,
      `Window ${days} days`,
      `Radius ${radiusKm} km`,
      `Focus ${themeConfig.label}`,
      speciesQuery.trim() ? `${themeConfig.planner.subjectLabel} ${speciesQuery.trim()}` : null,
    ],
    [date, days, manualLat, manualLng, radiusKm, speciesQuery, themeConfig]
  );

  const useBrowserLocation = () => {
    if (!navigator.geolocation) {
      setError('Browser geolocation is unavailable on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setManualLat(Number(position.coords.latitude.toFixed(4)));
        setManualLng(Number(position.coords.longitude.toFixed(4)));
      },
      () => {
        setError('Unable to access browser location right now.');
      }
    );
  };

  return (
    <Grow in>
      <Container className="atlas-route-shell">
        <div className="atlas-page-header">
          <span className="atlas-eyebrow">{themeConfig.planner.eyebrow}</span>
          <h1>{themeConfig.planner.title}</h1>
          <p>{themeConfig.planner.description}</p>
        </div>
        <ThemeSwitch className="atlas-theme-switch--page" />

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Paper elevation={6} className="atlas-sidebar-card atlas-signal-panel">
                <div className="atlas-panel-header">
                  <div className="atlas-sidebar-copy atlas-sidebar-copy--flush">
                  <h3>Build a focused field query</h3>
                  <p>
                    Start with place, date, and radius. Then use the rest of the page to decide
                    whether this window looks worth the trip, watch, or teaching moment.
                  </p>
                  </div>
                <button type="button" className="atlas-secondary-link atlas-control-button" onClick={useBrowserLocation}>
                  Use my location
                </button>
              </div>

              <div className="atlas-journey-grid">
                <article className="atlas-journey-card">
                  <strong>Start with place</strong>
                  <span>Anchor the plan to the coastline, bay, or region you can realistically reach.</span>
                </article>
                <article className="atlas-journey-card">
                  <strong>Start with season</strong>
                  <span>Shift the date and compare how the same region changes across the season.</span>
                </article>
                <article className="atlas-journey-card">
                  <strong>Start with {themeConfig.planner.subjectLabel}</strong>
                  <span>Use the subject field when you already know the species, system, phenomenon, or tradition you want to track.</span>
                </article>
              </div>

              <div className="atlas-control-grid">
                <label className="atlas-control-label">
                  1. Latitude
                  <input
                    className="atlas-control-input"
                    type="number"
                    step="0.01"
                    value={manualLat}
                    onChange={(event) => setManualLat(event.target.value)}
                  />
                </label>
                <label className="atlas-control-label">
                  1. Longitude
                  <input
                    className="atlas-control-input"
                    type="number"
                    step="0.01"
                    value={manualLng}
                    onChange={(event) => setManualLng(event.target.value)}
                  />
                </label>
                <label className="atlas-control-label">
                  1. Date
                  <input
                    className="atlas-control-input"
                    type="date"
                    value={date}
                    onChange={(event) => setDate(event.target.value)}
                  />
                </label>
                <label className="atlas-control-label">
                  2. Days ahead
                  <select
                    className="atlas-control-input"
                    value={days}
                    onChange={(event) => setDays(Number(event.target.value))}
                  >
                    <option value={7}>7</option>
                    <option value={14}>14</option>
                    <option value={30}>30</option>
                  </select>
                </label>
                <label className="atlas-control-label">
                  2. Radius km
                  <select
                    className="atlas-control-input"
                    value={radiusKm}
                    onChange={(event) => setRadiusKm(Number(event.target.value))}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <label className="atlas-control-label">
                  2. Intent
                  <select
                    className="atlas-control-input"
                    value={intent}
                    onChange={(event) => setIntent(event.target.value)}
                  >
                    {INTENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="atlas-control-label atlas-control-label--wide">
                  3. Start from a {themeConfig.planner.subjectLabel}
                  <input
                    className="atlas-control-input"
                    type="text"
                    placeholder={themeConfig.planner.subjectPlaceholder}
                    value={speciesQuery}
                    onChange={(event) => setSpeciesQuery(event.target.value)}
                  />
                </label>
              </div>

              <div className="atlas-pill-row atlas-pill-row--compact">
                {querySummary.filter(Boolean).map((item) => (
                  <span key={item} className="atlas-pill">
                    {item}
                  </span>
                ))}
              </div>

              <div className="atlas-cta-row">
                <Link
                  className="atlas-secondary-link"
                  to={buildThemeHref(
                    themeConfig.planner.followUpPath || '/species',
                    location.search,
                    activeTheme,
                    themeConfig.planner.followUpOverrides || { page: 1 }
                  )}
                >
                  {themeConfig.planner.followUpCta}
                </Link>
              </div>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <div className="atlas-detail-stack">
              <Paper elevation={6} className="atlas-detail-card">
                <span className="atlas-card-kicker">What is happening nearby</span>
                <p className="atlas-card-copy">
                  Start here for the broad picture, then move down into the context cards and notes
                  once you know which signals deserve attention.
                </p>
                {loading ? <div className="atlas-empty-state atlas-empty-state--compact">Loading nearby events…</div> : null}
                {!loading && error ? <div className="atlas-empty-state atlas-empty-state--compact">{error}</div> : null}
                {!loading && !error ? (
                  <div className="atlas-mini-stats">
                    <div>
                      <strong>{eventsPayload?.summary?.event_count ?? 0}</strong>
                      <span>events</span>
                    </div>
                    <div>
                      <strong>{eventsPayload?.summary?.active_count ?? 0}</strong>
                      <span>active</span>
                    </div>
                    <div>
                      <strong>{eventsPayload?.summary?.upcoming_count ?? 0}</strong>
                      <span>upcoming</span>
                    </div>
                  </div>
                ) : null}
                {!loading && !error && nearbyEvents.length ? (
                  <div className="atlas-upcoming-grid">
                    {nearbyEvents.map((item) => (
                      <article key={item.id || item.item_type_id || item.title} className="atlas-upcoming-card">
                        <span className="atlas-card-kicker">{item.category_label || item.category || 'Signal'}</span>
                        <h4>{item.title}</h4>
                        <p>{item.summary || item.description || 'No summary available yet.'}</p>
                        <div className="atlas-detail-stack">
                          <span>
                            <strong>Status</strong>
                            {item.status || 'scheduled'}
                          </span>
                          <span>
                            <strong>Starts</strong>
                            {prettyDate(item.starts_at || item.start_ts)}
                          </span>
                          <span>
                            <strong>Distance</strong>
                            {item.distance_km != null ? `${item.distance_km.toFixed(1)} km` : 'regional'}
                          </span>
                        </div>
                        <EventLearningCard item={item} />
                      </article>
                    ))}
                  </div>
                ) : null}
              </Paper>

              <Paper elevation={6} className="atlas-detail-card">
                <span className="atlas-card-kicker">{themeConfig.planner.contextTitle}</span>
                <p className="atlas-card-copy">
                  {activeTheme === 'wildlife'
                    ? 'These are habitat-and-season planning signals, not guaranteed sightings. They are meant to help you move from one chosen anchor point into adjacent possibilities: what else may emerge, which groups become more likely, and what cues to watch for in the field.'
                    : `These cards keep the planner grounded in ${themeConfig.label.toLowerCase()} context so you can relate a place and date to the systems, phenomena, or traditions that shape the window.`}
                </p>
                {strataCards.length ? (
                  <div className="atlas-upcoming-grid atlas-upcoming-grid--single">
                    {strataCards.map((card) => (
                      <article key={card.id || card.title} className="atlas-upcoming-card">
                        <h4>{card.label || card.title}</h4>
                        <div className="atlas-detail-list">
                          <span>
                            <strong>{activeTheme === 'wildlife' ? 'Likely groups' : 'Context'}</strong>
                            {Array.isArray(card.likely_species_groups)
                              ? card.likely_species_groups.join(', ')
                              : card.body || 'Not available'}
                          </span>
                        </div>
                        {activeTheme === 'wildlife' ? (
                          <>
                            <div className="atlas-detail-list">
                              <span>
                                <strong>Look for</strong>
                                {Array.isArray(card.cues) ? card.cues.join(', ') : 'Not available'}
                              </span>
                            </div>
                            <div className="atlas-detail-list">
                              <span>
                                <strong>Timing</strong>
                                {Array.isArray(card.timing_notes) ? card.timing_notes.join(' ') : 'No timing notes yet'}
                              </span>
                            </div>
                          </>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="atlas-empty-state atlas-empty-state--compact">Context is not available for this query yet.</div>
                )}
              </Paper>

              <Paper elevation={6} className="atlas-detail-card">
                <span className="atlas-card-kicker">{themeConfig.planner.notesTitle}</span>
                <div className="atlas-upcoming-grid atlas-upcoming-grid--single">
                  <article className="atlas-upcoming-card">
                    <h4>{activeTheme === 'wildlife' ? 'Wildlife context' : `${themeConfig.label} context`}</h4>
                    <ul className="atlas-bullet-list atlas-bullet-list--compact">
                      {noteItems.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </article>
                  <article className="atlas-upcoming-card">
                    <h4>How to use this plan</h4>
                    <ul className="atlas-bullet-list atlas-bullet-list--compact">
                      <li>Check the nearby events first so you know whether the date window is actually alive.</li>
                      <li>Use the context cards to understand why this window matters, not just that it exists.</li>
                      <li>Adjust radius before you change theme so you can tell whether distance or timing is the limiting factor.</li>
                      <li>Jump into the catalog when you need subject background that will not fit inside a short event card.</li>
                    </ul>
                  </article>
                </div>
              </Paper>
            </div>
          </Grid>
        </Grid>
      </Container>
    </Grow>
  );
};
